import type {
  AboutSection,
  BuilderDocument,
  BuilderDocumentRecord,
  BuilderLinkField,
  BuilderMenuLocation,
  BuilderPageStatus,
  BuilderPageType,
  BuilderThemePresetId,
  BuilderStarterSectionTemplate,
  BuilderSection,
  BuilderSectionType,
  ContactSection,
  CtaSection,
  FaqSection,
  FeaturesSection,
  FooterSection,
  GallerySection,
  HeroSection,
  ServicesSection,
  StatsSection,
  StepsSection,
  TestimonialsSection
} from '@shared/types';
import { slugifyLabel } from '@shared/utils/slug';
import {
  builderThemePresetCatalog,
  getBuilderThemeStarterPageTemplate,
  isBuilderThemePresetId,
  normalizeBuilderThemePresetId
} from './presets';

function createId(prefix: string): string {
  const cryptoRef = globalThis.crypto;
  if (cryptoRef && typeof cryptoRef.randomUUID === 'function') {
    return `${prefix}-${cryptoRef.randomUUID()}`;
  }

  return `${prefix}-${Math.random().toString(36).slice(2, 10)}`;
}

function createLink(label = '', href = '', style: BuilderLinkField['style'] = 'primary'): BuilderLinkField {
  return {
    label,
    href,
    style
  };
}

function createSectionBase<TSection extends BuilderSection>(
  section: Omit<TSection, 'id' | 'enabled' | 'notes' | 'aiHints' | 'anchor'>
): TSection {
  return {
    ...section,
    id: createId('section'),
    enabled: true,
    notes: null,
    aiHints: [],
    anchor: null
  } as unknown as TSection;
}

type BuilderSectionByType<TType extends BuilderSectionType> = Extract<BuilderSection, { type: TType }>;

export const builderPageTypeCatalog: Array<{ value: BuilderPageType; labelKey: string; descriptionKey: string }> = [
  {
    value: 'homepage',
    labelKey: 'project.builder.pageTypes.homepage.label',
    descriptionKey: 'project.builder.pageTypes.homepage.description'
  },
  {
    value: 'standard',
    labelKey: 'project.builder.pageTypes.standard.label',
    descriptionKey: 'project.builder.pageTypes.standard.description'
  },
  {
    value: 'contact',
    labelKey: 'project.builder.pageTypes.contact.label',
    descriptionKey: 'project.builder.pageTypes.contact.description'
  }
];
export { builderThemePresetCatalog, isBuilderThemePresetId, normalizeBuilderThemePresetId } from './presets';

export function isBuilderPageType(value: string | null | undefined): value is BuilderPageType {
  return value === 'homepage' || value === 'standard' || value === 'contact';
}

export function isBuilderMenuLocation(value: string | null | undefined): value is BuilderMenuLocation {
  return value === 'none' || value === 'primary' || value === 'footer';
}

export function normalizeBuilderMenuLocation(
  value: string | null | undefined,
  fallback: BuilderMenuLocation = 'primary'
): BuilderMenuLocation {
  return isBuilderMenuLocation(value) ? value : fallback;
}

export function inferBuilderPageType(input: {
  pageType?: string | null | undefined;
  slug?: string | null | undefined;
  title?: string | null | undefined;
  sections?: BuilderSection[] | null | undefined;
}): BuilderPageType {
  if (isBuilderPageType(input.pageType)) {
    return input.pageType;
  }

  const slug = slugifyLabel(input.slug?.trim() || input.title?.trim() || '');
  if (slug === 'homepage' || slug === 'home') {
    return 'homepage';
  }

  if (slug.includes('contact') || input.sections?.some((section) => section.type === 'contact')) {
    return 'contact';
  }

  return 'standard';
}

export function normalizeBuilderPageType(
  value: string | null | undefined,
  fallback: BuilderPageType = 'standard'
): BuilderPageType {
  return isBuilderPageType(value) ? value : fallback;
}

export function createDefaultSection<TType extends BuilderSectionType>(type: TType): BuilderSectionByType<TType>;
export function createDefaultSection(type: BuilderSectionType): BuilderSection {
  switch (type) {
    case 'hero':
      return createSectionBase<HeroSection>({
        type: 'hero',
        variant: 'split',
        props: {
          eyebrow: 'Mesaj clar',
          heading: 'Titlu principal pentru pagina ta',
          subheading: 'Descriere scurta, clara si orientata spre beneficii.',
          primaryCta: createLink('Solicita oferta', '/contact', 'primary'),
          secondaryCta: createLink('Vezi serviciile', '#servicii', 'secondary'),
          media: { assetId: null, altText: null },
          alignment: 'start'
        }
      });
    case 'about':
      return createSectionBase<AboutSection>({
        type: 'about',
        variant: 'default',
        props: {
          heading: 'Despre noi',
          intro: 'Introducere scurta care seteaza contextul.',
          body: 'Spune pe scurt cine este brandul, ce face si de ce conteaza.',
          highlights: ['Experienta relevanta', 'Proces clar', 'Rezultate masurabile']
        }
      });
    case 'services':
      return createSectionBase<ServicesSection>({
        type: 'services',
        variant: 'cards',
        props: {
          heading: 'Servicii',
          intro: 'Selecteaza serviciile principale pe care le oferim.',
          items: [
            { id: createId('service'), title: 'Serviciu 1', description: 'Descriere scurta.', icon: { assetId: null, altText: null } },
            { id: createId('service'), title: 'Serviciu 2', description: 'Descriere scurta.', icon: { assetId: null, altText: null } },
            { id: createId('service'), title: 'Serviciu 3', description: 'Descriere scurta.', icon: { assetId: null, altText: null } }
          ]
        }
      });
    case 'features':
      return createSectionBase<FeaturesSection>({
        type: 'features',
        variant: 'grid',
        props: {
          heading: 'Avantaje',
          intro: 'Beneficii sau puncte diferentiatoare.',
          items: [
            { id: createId('feature'), title: 'Avantaj 1', description: 'Descriere succinta.' },
            { id: createId('feature'), title: 'Avantaj 2', description: 'Descriere succinta.' },
            { id: createId('feature'), title: 'Avantaj 3', description: 'Descriere succinta.' }
          ]
        }
      });
    case 'steps':
      return createSectionBase<StepsSection>({
        type: 'steps',
        variant: 'numbered',
        props: {
          heading: 'Proces',
          intro: 'Arata pasii principali pentru colaborare sau livrare.',
          items: [
            { id: createId('step'), title: 'Pasul 1', description: 'Definim obiectivele.' },
            { id: createId('step'), title: 'Pasul 2', description: 'Construim solutia.' },
            { id: createId('step'), title: 'Pasul 3', description: 'Lansam si optimizam.' }
          ]
        }
      });
    case 'stats':
      return createSectionBase<StatsSection>({
        type: 'stats',
        variant: 'band',
        props: {
          heading: 'Cifre rapide',
          intro: 'Afiseaza rezultate sau date relevante.',
          items: [
            { id: createId('stat'), value: '10+', label: 'ani experienta' },
            { id: createId('stat'), value: '150+', label: 'proiecte' },
            { id: createId('stat'), value: '24h', label: 'timp raspuns' }
          ]
        }
      });
    case 'testimonials':
      return createSectionBase<TestimonialsSection>({
        type: 'testimonials',
        variant: 'cards',
        props: {
          heading: 'Testimoniale',
          intro: 'Feedback de la clienti sau parteneri.',
          items: [
            { id: createId('testimonial'), quote: 'Colaborare excelenta.', author: 'Client 1', role: 'Administrator' },
            { id: createId('testimonial'), quote: 'Proces foarte clar.', author: 'Client 2', role: 'Manager' }
          ]
        }
      });
    case 'faq':
      return createSectionBase<FaqSection>({
        type: 'faq',
        variant: 'accordion',
        props: {
          heading: 'Intrebari frecvente',
          intro: 'Raspunsuri rapide pentru cele mai comune intrebari.',
          items: [
            { id: createId('faq'), question: 'Cat dureaza?', answer: 'Depinde de scop si complexitate.' },
            { id: createId('faq'), question: 'Ce include?', answer: 'Definim clar livrabilele in brief.' }
          ]
        }
      });
    case 'cta':
      return createSectionBase<CtaSection>({
        type: 'cta',
        variant: 'banner',
        props: {
          heading: 'Hai sa discutam proiectul',
          body: 'Propune urmatorul pas clar pentru utilizator.',
          primaryCta: createLink('Trimite mesaj', '/contact', 'primary'),
          secondaryCta: createLink('Vezi detalii', '#despre', 'secondary')
        }
      });
    case 'contact':
      return createSectionBase<ContactSection>({
        type: 'contact',
        variant: 'details-form',
        props: {
          heading: 'Contact',
          intro: 'Date de contact si optiuni de interactiune.',
          address: 'Strada exemplu nr. 10, Brasov',
          phone: '+40 700 000 000',
          email: 'contact@exemplu.ro',
          formEnabled: false,
          mapLabel: 'Harta locatie'
        }
      });
    case 'gallery':
      return createSectionBase<GallerySection>({
        type: 'gallery',
        variant: 'grid',
        props: {
          heading: 'Galerie',
          intro: 'Imagini relevante pentru proiect sau brand.',
          items: [
            { id: createId('gallery'), image: { assetId: null, altText: null }, caption: 'Cadru 1' },
            { id: createId('gallery'), image: { assetId: null, altText: null }, caption: 'Cadru 2' },
            { id: createId('gallery'), image: { assetId: null, altText: null }, caption: 'Cadru 3' }
          ]
        }
      });
    case 'footer':
      return createSectionBase<FooterSection>({
        type: 'footer',
        variant: 'simple',
        props: {
          companyName: 'Nume companie',
          tagline: 'Mesaj scurt de inchidere.',
          links: [
            { id: createId('footer-link'), label: 'Acasa', href: '/' },
            { id: createId('footer-link'), label: 'Servicii', href: '#servicii' },
            { id: createId('footer-link'), label: 'Contact', href: '#contact' }
          ],
          contactEmail: 'contact@exemplu.ro',
          contactPhone: '+40 700 000 000'
        }
      });
  }
}

function createStarterLinkField(
  value: unknown,
  fallback: BuilderLinkField
): BuilderLinkField {
  if (!value || typeof value !== 'object') {
    return fallback;
  }

  const candidate = value as Partial<BuilderLinkField>;
  return {
    label: typeof candidate.label === 'string' ? candidate.label : fallback.label,
    href: typeof candidate.href === 'string' ? candidate.href : fallback.href,
    style: candidate.style === 'secondary' ? 'secondary' : 'primary'
  };
}

function createStarterSectionFromTemplate(template: BuilderStarterSectionTemplate): BuilderSection {
  switch (template.type) {
    case 'hero': {
      const base = createDefaultSection('hero');
      const props = template.props as Partial<HeroSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          eyebrow: typeof props.eyebrow === 'string' ? props.eyebrow : base.props.eyebrow,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          subheading: typeof props.subheading === 'string' ? props.subheading : base.props.subheading,
          primaryCta: createStarterLinkField(props.primaryCta, base.props.primaryCta),
          secondaryCta: createStarterLinkField(props.secondaryCta, base.props.secondaryCta),
          media: {
            assetId:
              props.media && typeof props.media === 'object' && 'assetId' in props.media
                ? ((props.media.assetId as string | null | undefined) ?? null)
                : base.props.media.assetId,
            altText:
              props.media && typeof props.media === 'object' && 'altText' in props.media
                ? ((props.media.altText as string | null | undefined) ?? null)
                : base.props.media.altText
          },
          alignment: props.alignment === 'center' || props.alignment === 'end' ? props.alignment : base.props.alignment
        }
      } satisfies HeroSection;
    }
    case 'about': {
      const base = createDefaultSection('about');
      const props = template.props as Partial<AboutSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          body: typeof props.body === 'string' ? props.body : base.props.body,
          highlights: Array.isArray(props.highlights)
            ? props.highlights.map((item) => String(item))
            : base.props.highlights
        }
      } satisfies AboutSection;
    }
    case 'services': {
      const base = createDefaultSection('services');
      const props = template.props as Partial<ServicesSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('service'),
                title: String(item.title ?? ''),
                description: String(item.description ?? ''),
                icon: {
                  assetId:
                    item.icon && typeof item.icon === 'object' && 'assetId' in item.icon
                      ? ((item.icon.assetId as string | null | undefined) ?? null)
                      : null,
                  altText:
                    item.icon && typeof item.icon === 'object' && 'altText' in item.icon
                      ? ((item.icon.altText as string | null | undefined) ?? null)
                      : null
                }
              }))
            : base.props.items
        }
      } satisfies ServicesSection;
    }
    case 'features': {
      const base = createDefaultSection('features');
      const props = template.props as Partial<FeaturesSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('feature'),
                title: String(item.title ?? ''),
                description: String(item.description ?? '')
              }))
            : base.props.items
        }
      } satisfies FeaturesSection;
    }
    case 'steps': {
      const base = createDefaultSection('steps');
      const props = template.props as Partial<StepsSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('step'),
                title: String(item.title ?? ''),
                description: String(item.description ?? '')
              }))
            : base.props.items
        }
      } satisfies StepsSection;
    }
    case 'stats': {
      const base = createDefaultSection('stats');
      const props = template.props as Partial<StatsSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('stat'),
                value: String(item.value ?? ''),
                label: String(item.label ?? '')
              }))
            : base.props.items
        }
      } satisfies StatsSection;
    }
    case 'testimonials': {
      const base = createDefaultSection('testimonials');
      const props = template.props as Partial<TestimonialsSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('testimonial'),
                quote: String(item.quote ?? ''),
                author: String(item.author ?? ''),
                role: String(item.role ?? '')
              }))
            : base.props.items
        }
      } satisfies TestimonialsSection;
    }
    case 'faq': {
      const base = createDefaultSection('faq');
      const props = template.props as Partial<FaqSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('faq'),
                question: String(item.question ?? ''),
                answer: String(item.answer ?? '')
              }))
            : base.props.items
        }
      } satisfies FaqSection;
    }
    case 'cta': {
      const base = createDefaultSection('cta');
      const props = template.props as Partial<CtaSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          body: typeof props.body === 'string' ? props.body : base.props.body,
          primaryCta: createStarterLinkField(props.primaryCta, base.props.primaryCta),
          secondaryCta: createStarterLinkField(props.secondaryCta, base.props.secondaryCta)
        }
      } satisfies CtaSection;
    }
    case 'contact': {
      const base = createDefaultSection('contact');
      const props = template.props as Partial<ContactSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          address: typeof props.address === 'string' ? props.address : base.props.address,
          phone: typeof props.phone === 'string' ? props.phone : base.props.phone,
          email: typeof props.email === 'string' ? props.email : base.props.email,
          formEnabled: typeof props.formEnabled === 'boolean' ? props.formEnabled : base.props.formEnabled,
          mapLabel: typeof props.mapLabel === 'string' ? props.mapLabel : base.props.mapLabel
        }
      } satisfies ContactSection;
    }
    case 'gallery': {
      const base = createDefaultSection('gallery');
      const props = template.props as Partial<GallerySection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          heading: typeof props.heading === 'string' ? props.heading : base.props.heading,
          intro: typeof props.intro === 'string' ? props.intro : base.props.intro,
          items: Array.isArray(props.items)
            ? props.items.map((item) => ({
                id: createId('gallery'),
                image: {
                  assetId:
                    item.image && typeof item.image === 'object' && 'assetId' in item.image
                      ? ((item.image.assetId as string | null | undefined) ?? null)
                      : null,
                  altText:
                    item.image && typeof item.image === 'object' && 'altText' in item.image
                      ? ((item.image.altText as string | null | undefined) ?? null)
                      : null
                },
                caption: String(item.caption ?? '')
              }))
            : base.props.items
        }
      } satisfies GallerySection;
    }
    case 'footer': {
      const base = createDefaultSection('footer');
      const props = template.props as Partial<FooterSection['props']>;
      return {
        ...base,
        props: {
          ...base.props,
          companyName: typeof props.companyName === 'string' ? props.companyName : base.props.companyName,
          tagline: typeof props.tagline === 'string' ? props.tagline : base.props.tagline,
          links: Array.isArray(props.links)
            ? props.links.map((item) => ({
                id: createId('footer-link'),
                label: String(item.label ?? ''),
                href: String(item.href ?? '')
              }))
            : base.props.links,
          contactEmail: typeof props.contactEmail === 'string' ? props.contactEmail : base.props.contactEmail,
          contactPhone: typeof props.contactPhone === 'string' ? props.contactPhone : base.props.contactPhone
        }
      } satisfies FooterSection;
    }
  }
}

export function createStarterSections(
  pageType: BuilderPageType,
  presetId: BuilderThemePresetId = 'corporate_services'
): BuilderSection[] {
  const starterTemplate = getBuilderThemeStarterPageTemplate(presetId, pageType);
  if (starterTemplate) {
    return starterTemplate.sections.map((section) => createStarterSectionFromTemplate(section));
  }

  switch (pageType) {
    case 'homepage':
      return [createDefaultSection('hero'), createDefaultSection('about'), createDefaultSection('services'), createDefaultSection('cta'), createDefaultSection('footer')];
    case 'contact':
      return [createDefaultSection('hero'), createDefaultSection('contact'), createDefaultSection('faq'), createDefaultSection('footer')];
    case 'standard':
    default:
      return [createDefaultSection('hero'), createDefaultSection('about'), createDefaultSection('cta'), createDefaultSection('footer')];
  }
}

export const builderSectionCatalog: Array<{ type: BuilderSectionType; labelKey: string; descriptionKey: string }> = [
  {
    type: 'hero',
    labelKey: 'project.builder.sectionCatalog.hero.label',
    descriptionKey: 'project.builder.sectionCatalog.hero.description'
  },
  {
    type: 'about',
    labelKey: 'project.builder.sectionCatalog.about.label',
    descriptionKey: 'project.builder.sectionCatalog.about.description'
  },
  {
    type: 'services',
    labelKey: 'project.builder.sectionCatalog.services.label',
    descriptionKey: 'project.builder.sectionCatalog.services.description'
  },
  {
    type: 'features',
    labelKey: 'project.builder.sectionCatalog.features.label',
    descriptionKey: 'project.builder.sectionCatalog.features.description'
  },
  {
    type: 'steps',
    labelKey: 'project.builder.sectionCatalog.steps.label',
    descriptionKey: 'project.builder.sectionCatalog.steps.description'
  },
  {
    type: 'stats',
    labelKey: 'project.builder.sectionCatalog.stats.label',
    descriptionKey: 'project.builder.sectionCatalog.stats.description'
  },
  {
    type: 'testimonials',
    labelKey: 'project.builder.sectionCatalog.testimonials.label',
    descriptionKey: 'project.builder.sectionCatalog.testimonials.description'
  },
  {
    type: 'faq',
    labelKey: 'project.builder.sectionCatalog.faq.label',
    descriptionKey: 'project.builder.sectionCatalog.faq.description'
  },
  {
    type: 'cta',
    labelKey: 'project.builder.sectionCatalog.cta.label',
    descriptionKey: 'project.builder.sectionCatalog.cta.description'
  },
  {
    type: 'contact',
    labelKey: 'project.builder.sectionCatalog.contact.label',
    descriptionKey: 'project.builder.sectionCatalog.contact.description'
  },
  {
    type: 'gallery',
    labelKey: 'project.builder.sectionCatalog.gallery.label',
    descriptionKey: 'project.builder.sectionCatalog.gallery.description'
  },
  {
    type: 'footer',
    labelKey: 'project.builder.sectionCatalog.footer.label',
    descriptionKey: 'project.builder.sectionCatalog.footer.description'
  }
];

export function duplicateBuilderSection(section: BuilderSection): BuilderSection {
  return {
    ...structuredClone(section),
    id: createId('section')
  };
}

export function summarizeBuilderSection(section: BuilderSection): string {
  switch (section.type) {
    case 'hero':
      return section.props.heading;
    case 'about':
    case 'services':
    case 'features':
    case 'steps':
    case 'stats':
    case 'testimonials':
    case 'faq':
    case 'cta':
    case 'contact':
    case 'gallery':
      return section.props.heading;
    case 'footer':
      return section.props.companyName;
  }
}

export function createBuilderDocument(input: {
  title: string;
  slug?: string;
  pageId?: string;
  pageType?: BuilderPageType;
  status?: BuilderPageStatus;
  menuLocation?: BuilderMenuLocation;
  sections?: BuilderSection[];
  logoAssetId?: string | null;
  iconAssetId?: string | null;
  presetId?: BuilderThemePresetId;
}): BuilderDocument {
  const title = input.title.trim() || 'Pagina noua';
  const pageType = normalizeBuilderPageType(input.pageType, inferBuilderPageType({ slug: input.slug, title }));
  const starterTemplate = getBuilderThemeStarterPageTemplate(input.presetId, pageType);

  return {
    builderVersion: 1,
    compatibility: {
      targets: ['wordpress'],
      lastValidatedAt: null
    },
    pageMeta: {
      pageId: input.pageId ?? createId('page'),
      slug: input.slug?.trim() ? slugifyLabel(input.slug) : slugifyLabel(title),
      title,
      status: input.status ?? 'draft',
      menuLocation: normalizeBuilderMenuLocation(input.menuLocation, starterTemplate?.menuLocation ?? 'none'),
      seoTitle: starterTemplate?.seoTitle ?? title,
      seoDescription: starterTemplate?.seoDescription ?? '',
      pageType
    },
    themeRefs: {
      logoAssetId: input.logoAssetId ?? null,
      iconAssetId: input.iconAssetId ?? null,
      tone: 'default'
    },
    sections: input.sections ?? createStarterSections(pageType, input.presetId),
    ai: {
      notes: null,
      sourcePrompt: null,
      generationSource: 'manual'
    }
  };
}

export function createFallbackBuilderDocument(input: {
  projectName: string;
  projectSummary: string;
  projectSlug: string;
  logoAssetId: string | null;
  iconAssetId: string | null;
  primaryDomain: string | null;
  localDomain: string | null;
  contactEmail?: string | null;
  presetId?: BuilderThemePresetId;
}): BuilderDocument {
  const presetSections = createStarterSections('homepage', input.presetId);
  const documentInput: Parameters<typeof createBuilderDocument>[0] = {
    title: 'Homepage',
    slug: input.projectSlug,
    pageType: 'homepage',
    logoAssetId: input.logoAssetId,
    iconAssetId: input.iconAssetId,
    sections: presetSections.map((section) => {
      switch (section.type) {
        case 'hero':
          return {
            ...section,
            props: {
              ...section.props,
              eyebrow: input.primaryDomain ?? input.localDomain ?? section.props.eyebrow,
              heading: input.projectName,
              subheading: input.projectSummary || section.props.subheading,
              primaryCta: createLink(section.props.primaryCta.label || 'Contact', '#contact', section.props.primaryCta.style),
              secondaryCta: createLink(
                section.props.secondaryCta.label || 'Detalii',
                section.props.secondaryCta.href || '#despre',
                section.props.secondaryCta.style
              ),
              media: {
                assetId: input.logoAssetId ?? section.props.media.assetId,
                altText: input.projectName
              }
            }
          } as HeroSection;
        case 'about':
          return {
            ...section,
            props: {
              ...section.props,
              body: input.projectSummary || section.props.body
            }
          } as AboutSection;
        case 'contact':
          return {
            ...section,
            props: {
              ...section.props,
              address: input.primaryDomain ?? input.localDomain ?? section.props.address,
              email: input.contactEmail ?? section.props.email
            }
          } as ContactSection;
        case 'footer':
          return {
            ...section,
            props: {
              ...section.props,
              companyName: input.projectName,
              contactEmail: input.contactEmail ?? section.props.contactEmail
            }
          } as FooterSection;
        default:
          return section;
      }
    })
  };
  const document = createBuilderDocument(input.presetId ? { ...documentInput, presetId: input.presetId } : documentInput);

  document.pageMeta.title = 'Homepage';
  document.pageMeta.seoTitle = `${input.projectName} | Homepage`;
  document.pageMeta.seoDescription = input.projectSummary || '';
  return document;
}

export function normalizeBuilderDocument(document: BuilderDocument): BuilderDocument {
  const nextDocument = structuredClone(document);
  nextDocument.pageMeta.pageType = normalizeBuilderPageType(
    (nextDocument.pageMeta as { pageType?: string | null }).pageType,
    inferBuilderPageType({
      pageType: (nextDocument.pageMeta as { pageType?: string | null }).pageType,
      slug: nextDocument.pageMeta.slug,
      title: nextDocument.pageMeta.title,
      sections: nextDocument.sections
    })
  );
  nextDocument.pageMeta.menuLocation = normalizeBuilderMenuLocation(
    (nextDocument.pageMeta as { menuLocation?: string | null }).menuLocation,
    'primary'
  );

  return nextDocument;
}

export function syncBuilderRecordWithDocument(record: BuilderDocumentRecord, document: BuilderDocument): BuilderDocumentRecord {
  const nextDocument = normalizeBuilderDocument(document);

  return {
    ...record,
    pageId: nextDocument.pageMeta.pageId,
    title: nextDocument.pageMeta.title,
    slug: nextDocument.pageMeta.slug,
    status: nextDocument.pageMeta.status,
    schemaVersion: nextDocument.builderVersion,
    document: nextDocument
  };
}

export function normalizeBuilderRecord(record: BuilderDocumentRecord): BuilderDocumentRecord {
  const document = normalizeBuilderDocument(record.document);

  return {
    ...record,
    pageId: document.pageMeta.pageId,
    title: document.pageMeta.title,
    slug: document.pageMeta.slug,
    status: document.pageMeta.status,
    schemaVersion: document.builderVersion,
    document
  };
}
